import React, { useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { markCookRated, selectRecipeVerdicts } from '../../redux/recipeMetadataSlice';
import { VerdictRows } from '../household/VerdictRows';
import { abandonSession } from '../../redux/cookingSessionSlice';
import { useTranslation } from 'react-i18next';

interface Props {
  recipeId: string;
  recipeTitle: string;
  onClose: () => void;
}

export const CookingDoneScreen: React.FC<Props> = ({ recipeId, recipeTitle, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  // Read the verdicts from the store rather than mirroring them in local state
  // (#562): a local seed showed an empty answer on a recipe the household had
  // already judged elsewhere, and could disagree with what this screen wrote.
  const verdicts = useSelector((state: RootState) => selectRecipeVerdicts(state, recipeId));
  const answered = Object.keys(verdicts).length > 0;

  // The cook session counts as rated as soon as anyone at the table has
  // answered — VerdictRows writes on tap, so there is no submit to hook.
  useEffect(() => {
    if (answered) dispatch(markCookRated(recipeId));
  }, [answered, dispatch, recipeId]);

  const handleClose = () => {
    dispatch(abandonSession());
    onClose();
  };

  return (
    <View style={styles.container}>
      <SafeAreaView>
        <View style={styles.header}>
          <TouchableOpacity
            onPress={handleClose}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="close" size={24} color="#1b1c1a" />
          </TouchableOpacity>
        </View>
      </SafeAreaView>

      <View style={styles.body}>
        <Text style={styles.bonAppetit}>{t('cooking.enjoy')}</Text>
        <Text style={styles.subtitle} numberOfLines={2}>{recipeTitle}</Text>

        <Text style={styles.ratingLabel}>{t('cooking.likedRecipe')}</Text>
        <View style={styles.verdicts}>
          <VerdictRows recipeId={recipeId} surface="cook_done" />
        </View>
      </View>

      <SafeAreaView style={styles.footer}>
        {answered ? (
          <LinearGradient
            colors={['#864e2c', '#a36642']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.primaryButton}
          >
            <TouchableOpacity onPress={handleClose} style={styles.buttonContent}>
              <Text style={styles.primaryButtonText}>{t('household.verdict.done')}</Text>
            </TouchableOpacity>
          </LinearGradient>
        ) : (
          <TouchableOpacity style={styles.skipButton} onPress={handleClose}>
            <Text style={styles.skipButtonText}>{t('cooking.skipForNow')}</Text>
          </TouchableOpacity>
        )}
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fbf9f6',
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: '#ffffff',
  },
  body: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  bonAppetit: {
    fontSize: 48,
    fontFamily: 'Newsreader-Italic',
    fontStyle: 'italic',
    color: '#864e2c',
    textAlign: 'center',
    marginBottom: 12,
  },
  subtitle: {
    fontSize: 18,
    fontFamily: 'Newsreader-Regular',
    color: '#52443c',
    textAlign: 'center',
    marginBottom: 40,
  },
  ratingLabel: {
    fontSize: 14,
    fontFamily: 'Inter-Medium',
    color: '#85746b',
    textAlign: 'center',
    marginBottom: 20,
  },
  verdicts: {
    alignSelf: 'stretch',
  },
  footer: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    backgroundColor: '#ffffff',
    shadowColor: 'rgba(82, 68, 60, 0.08)',
    shadowOffset: { width: 0, height: -2 },
    shadowRadius: 8,
    elevation: 4,
  },
  primaryButton: {
    borderRadius: 24,
    overflow: 'hidden',
    marginTop: 16,
  },
  buttonContent: {
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonText: {
    fontSize: 14,
    fontFamily: 'Inter-Medium',
    color: '#ffffff',
  },
  skipButton: {
    paddingVertical: 15,
    alignItems: 'center',
    marginTop: 16,
  },
  skipButtonText: {
    fontSize: 14,
    fontFamily: 'Inter-Medium',
    color: '#85746b',
  },
});
